/**
 * Geometry-side skinning primitives: the bone segments a placement defines, the legacy Euclidean
 * weight solver, and the small read-only queries the editor needs.
 *
 * Auto-skin v2 solves the weights in `src/rig/autoskin/` (voxel + geodesic + parametric solver);
 * `computeSkinWeights` here is the Euclidean predecessor, kept as the reference implementation the
 * autoskin tests measure against. The hand-paint brush helpers this file used to export are gone
 * with the brush itself — corrections are parametric now (`SkinParams`, region overrides).
 *
 * Everything here is pure: functions take geometry / `SkinData` and return new data. Nothing
 * touches the DOM or editor state. Output is deterministic — identical input produces
 * byte-identical typed arrays, which matters because `SkinData` is persisted (D21).
 *
 * Invariants upheld everywhere (SPEC "Skinning invariants"):
 *  - at most 4 influences per vertex,
 *  - weights per vertex sum to ~1,
 *  - only bone indices produced by the caller's `boneIndexOf` are ever emitted,
 *  - slots are ordered by descending weight; unused slots are `index 0, weight 0`.
 */

import { Float32BufferAttribute, Uint16BufferAttribute } from 'three';
import type { BufferGeometry } from 'three';

import { AppError, CANONICAL_BONE_IDS } from '../shared';
import type { CanonicalBoneId, CanonicalRigPlacement, SkinData, Vec3Tuple } from '../shared';

/** three.js skin attributes are always 4 wide. */
const SLOTS = 4;

/** A bone drawn as a line segment in model space; the unit skinning distances are measured to. */
export interface BoneSegment {
  id: CanonicalBoneId;
  head: Vec3Tuple;
  tail: Vec3Tuple;
}

export interface SkinWeightOptions {
  /** Influences kept per vertex, clamped to 1..4. Default 4. */
  maxInfluences?: number;
  /** Exponent `p` in `w = 1 / (d + eps)^p`. Higher = tighter falloff. Default 4. */
  falloffPower?: number;
  /** Multiplier applied to a wrong-side limb bone's weight. Default 0.05. */
  sideBias?: number;
  /** |x| below this counts as "centre" and gets no side penalty. Default 2% of the x extent. */
  sideMargin?: number;
  /** `eps` in the falloff, guarding against division by zero. Default 1e-4. */
  distanceEpsilon?: number;
  /** Copied into the returned `SkinData`. Default `''` (the caller usually sets it). */
  meshName?: string;
}

export interface VertexInfluence {
  boneIndex: number;
  weight: number;
}

// ---------------------------------------------------------------------------
// Bone segments from a placement
// ---------------------------------------------------------------------------

/** Bones whose tail is simply the next joint down the chain. */
const SEGMENT_CHILD: Partial<Record<CanonicalBoneId, CanonicalBoneId>> = {
  Hips: 'Spine',
  Spine: 'Chest',
  Chest: 'Neck',
  Neck: 'Head',
  LeftUpperArm: 'LeftLowerArm',
  LeftLowerArm: 'LeftHand',
  RightUpperArm: 'RightLowerArm',
  RightLowerArm: 'RightHand',
  LeftUpperLeg: 'LeftLowerLeg',
  LeftLowerLeg: 'LeftFoot',
  RightUpperLeg: 'RightLowerLeg',
  RightLowerLeg: 'RightFoot',
};

/** Leaf bones and the joint their synthetic tip is extrapolated from (D20). */
const TIP_FROM: Partial<Record<CanonicalBoneId, { from: CanonicalBoneId; scale: number }>> = {
  Head: { from: 'Neck', scale: 1 },
  LeftHand: { from: 'LeftLowerArm', scale: 0.5 },
  RightHand: { from: 'RightLowerArm', scale: 0.5 },
};

/** Reference height the foot tip offset is expressed for. */
const REFERENCE_HEIGHT = 1.8;
const FOOT_TIP_OFFSET: Vec3Tuple = [0, 0, 0.15];

/**
 * One segment per canonical bone except `Root` (which owns no vertices), in canonical hierarchy
 * order. Leaf bones (Head, hands, feet) get the synthetic tips described in plan D20 so they are
 * real segments rather than points.
 */
export function boneSegmentsFromPlacement(
  placement: CanonicalRigPlacement,
  modelHeight: number,
): BoneSegment[] {
  const joints = placement.jointWorldPositions;
  const footScale = (Number.isFinite(modelHeight) ? modelHeight : REFERENCE_HEIGHT) / REFERENCE_HEIGHT;
  const segments: BoneSegment[] = [];

  for (const id of CANONICAL_BONE_IDS) {
    if (id === 'Root') continue;
    const head = joints[id];
    if (!head) continue;

    const childId = SEGMENT_CHILD[id];
    if (childId) {
      const child = joints[childId];
      if (child) {
        segments.push({ id, head: [head[0], head[1], head[2]], tail: [child[0], child[1], child[2]] });
        continue;
      }
    }

    const tipSpec = TIP_FROM[id];
    if (tipSpec) {
      const from = joints[tipSpec.from];
      if (from) {
        const s = tipSpec.scale;
        segments.push({
          id,
          head: [head[0], head[1], head[2]],
          tail: [
            head[0] + (head[0] - from[0]) * s,
            head[1] + (head[1] - from[1]) * s,
            head[2] + (head[2] - from[2]) * s,
          ],
        });
        continue;
      }
    }

    // Feet (and any bone with neither a child nor an extrapolation source): fixed forward tip.
    segments.push({
      id,
      head: [head[0], head[1], head[2]],
      tail: [
        head[0] + FOOT_TIP_OFFSET[0] * footScale,
        head[1] + FOOT_TIP_OFFSET[1] * footScale,
        head[2] + FOOT_TIP_OFFSET[2] * footScale,
      ],
    });
  }

  return segments;
}

// ---------------------------------------------------------------------------
// Automatic weights
// ---------------------------------------------------------------------------

function distanceToSegment(
  px: number,
  py: number,
  pz: number,
  head: Vec3Tuple,
  tail: Vec3Tuple,
): number {
  const dx = tail[0] - head[0];
  const dy = tail[1] - head[1];
  const dz = tail[2] - head[2];
  const lenSq = dx * dx + dy * dy + dz * dz;

  let t = 0;
  if (lenSq > 1e-12) {
    t = ((px - head[0]) * dx + (py - head[1]) * dy + (pz - head[2]) * dz) / lenSq;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
  }

  return Math.hypot(px - (head[0] + dx * t), py - (head[1] + dy * t), pz - (head[2] + dz * t));
}

/** -1 for a Right* bone, +1 for a Left* bone, 0 for a centre bone. Canonical left is +X. */
function sideSign(id: CanonicalBoneId): number {
  if (id.startsWith('Left')) return 1;
  if (id.startsWith('Right')) return -1;
  return 0;
}

function positionAttribute(geometry: BufferGeometry) {
  const position = geometry.getAttribute('position');
  if (!position) {
    throw new AppError('SKIN_NO_POSITION', 'The mesh geometry has no position attribute.');
  }
  return position;
}

/**
 * Distance-based automatic skin weights (D7).
 *
 * For every vertex: distance to each bone segment, raw weight `1 / (d + eps)^p`, left/right
 * limbs on the wrong side of the body penalized by `sideBias`, the strongest `maxInfluences`
 * kept, normalized to 1. Ties are broken by segment order, so the result is deterministic.
 */
export function computeSkinWeights(
  geometry: BufferGeometry,
  segments: BoneSegment[],
  boneIndexOf: (id: CanonicalBoneId) => number,
  opts?: SkinWeightOptions,
): SkinData {
  if (segments.length === 0) {
    throw new AppError('SKIN_NO_SEGMENTS', 'Cannot compute skin weights without bone segments.');
  }

  const position = positionAttribute(geometry);
  const vertexCount = position.count;

  const maxInfluences = Math.max(1, Math.min(SLOTS, Math.floor(opts?.maxInfluences ?? SLOTS)));
  const falloffPower = opts?.falloffPower ?? 4;
  const sideBias = opts?.sideBias ?? 0.05;
  const epsilon = opts?.distanceEpsilon ?? 1e-4;

  // Bone indices are resolved once per segment: cheaper, and it keeps `boneIndexOf` call order
  // (and therefore any caching it does) independent of the vertex count.
  const boneIndices = segments.map((segment) => {
    const index = boneIndexOf(segment.id);
    if (!Number.isInteger(index) || index < 0) {
      throw new AppError(
        'SKIN_INVALID_BONE_INDEX',
        `No skeleton bone index for canonical bone "${segment.id}".`,
        { bone: segment.id, index },
      );
    }
    return index;
  });
  const sides = segments.map((segment) => sideSign(segment.id));

  let sideMargin = opts?.sideMargin;
  if (sideMargin === undefined) {
    let minX = Infinity;
    let maxX = -Infinity;
    for (let v = 0; v < vertexCount; v++) {
      const x = position.getX(v);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
    }
    const extent = Number.isFinite(maxX - minX) ? maxX - minX : 0;
    sideMargin = Math.max(extent * 0.02, 1e-6);
  }

  const skinIndex = new Uint16Array(vertexCount * SLOTS);
  const skinWeight = new Float32Array(vertexCount * SLOTS);

  const raw: VertexInfluence[] = segments.map(() => ({ boneIndex: 0, weight: 0 }));

  for (let v = 0; v < vertexCount; v++) {
    const px = position.getX(v);
    const py = position.getY(v);
    const pz = position.getZ(v);

    // Vertices clearly on one side of the body must not be pulled by the opposite limb.
    const vertexSide = px > sideMargin ? 1 : px < -sideMargin ? -1 : 0;

    for (let s = 0; s < segments.length; s++) {
      const segment = segments[s];
      const d = distanceToSegment(px, py, pz, segment.head, segment.tail);
      let w = 1 / Math.pow(d + epsilon, falloffPower);
      if (!Number.isFinite(w)) w = 0;
      const side = sides[s];
      if (side !== 0 && vertexSide !== 0 && side !== vertexSide) w *= sideBias;
      raw[s].boneIndex = boneIndices[s];
      raw[s].weight = w;
    }

    writeSlots(skinIndex, skinWeight, v, raw, maxInfluences);
  }

  return {
    meshName: opts?.meshName ?? '',
    vertexCount,
    skinIndex,
    skinWeight,
  };
}

/**
 * Sort by descending weight (segment order breaks ties), keep the strongest `maxInfluences`,
 * normalize to 1 and write into the two attribute arrays. Unused slots become index 0 weight 0.
 * A vertex with no usable weight at all falls back to a single full-weight influence on the
 * strongest candidate so the sum-to-one invariant always holds.
 */
function writeSlots(
  skinIndex: Uint16Array,
  skinWeight: Float32Array,
  vertex: number,
  candidates: VertexInfluence[],
  maxInfluences: number,
): void {
  const order = candidates.map((_, i) => i);
  order.sort((a, b) => candidates[b].weight - candidates[a].weight || a - b);

  const base = vertex * SLOTS;
  for (let slot = 0; slot < SLOTS; slot++) {
    skinIndex[base + slot] = 0;
    skinWeight[base + slot] = 0;
  }

  const kept = Math.min(maxInfluences, order.length);
  let sum = 0;
  for (let i = 0; i < kept; i++) {
    const w = candidates[order[i]].weight;
    if (w > 0 && Number.isFinite(w)) sum += w;
  }

  if (sum <= 0 || !Number.isFinite(sum)) {
    skinIndex[base] = candidates[order[0]].boneIndex;
    skinWeight[base] = 1;
    return;
  }

  for (let i = 0; i < kept; i++) {
    const candidate = candidates[order[i]];
    if (!(candidate.weight > 0) || !Number.isFinite(candidate.weight)) break;
    skinIndex[base + i] = candidate.boneIndex;
    skinWeight[base + i] = candidate.weight / sum;
  }
}

/**
 * Write a `SkinData` onto a geometry as the standard three.js `skinIndex`/`skinWeight` attributes.
 *
 * IN PLACE whenever the geometry already carries compatible attributes: auto-skin v2 re-solves the
 * weights on every parameter change, and other code (the viewport's heat overlay) holds the
 * attribute objects BY REFERENCE. Replacing them left those holders skinning with the weights from
 * generation time, so the overlay silently froze at the first solve. New attributes are created
 * only when none exist yet or the layout genuinely differs.
 */
export function applySkinDataToGeometry(geometry: BufferGeometry, skinData: SkinData): void {
  const position = positionAttribute(geometry);
  if (position.count !== skinData.vertexCount) {
    throw new AppError(
      'SKIN_VERTEX_COUNT_MISMATCH',
      'The skin weights do not match this mesh (different vertex count).',
      { geometryVertexCount: position.count, skinDataVertexCount: skinData.vertexCount },
    );
  }

  const index = geometry.getAttribute('skinIndex');
  if (
    index &&
    index.itemSize === SLOTS &&
    index.count === skinData.vertexCount &&
    index.array instanceof Uint16Array &&
    index.array.length === skinData.skinIndex.length
  ) {
    index.array.set(skinData.skinIndex);
    index.needsUpdate = true;
  } else {
    geometry.setAttribute('skinIndex', new Uint16BufferAttribute(skinData.skinIndex, SLOTS));
  }

  const weight = geometry.getAttribute('skinWeight');
  if (
    weight &&
    weight.itemSize === SLOTS &&
    weight.count === skinData.vertexCount &&
    weight.array instanceof Float32Array &&
    weight.array.length === skinData.skinWeight.length
  ) {
    weight.array.set(skinData.skinWeight);
    weight.needsUpdate = true;
  } else {
    geometry.setAttribute('skinWeight', new Float32BufferAttribute(skinData.skinWeight, SLOTS));
  }
}

// ---------------------------------------------------------------------------
// Correction helpers — all pure, all returning a new SkinData
// ---------------------------------------------------------------------------

/** Non-zero influences of one vertex, strongest first. Out-of-range vertices yield `[]`. */
export function getVertexInfluences(skinData: SkinData, vertex: number): VertexInfluence[] {
  if (!Number.isInteger(vertex) || vertex < 0 || vertex >= skinData.vertexCount) return [];
  const base = vertex * SLOTS;
  const out: VertexInfluence[] = [];
  for (let slot = 0; slot < SLOTS; slot++) {
    const weight = skinData.skinWeight[base + slot];
    if (weight > 0) out.push({ boneIndex: skinData.skinIndex[base + slot], weight });
  }
  out.sort((a, b) => b.weight - a.weight || a.boneIndex - b.boneIndex);
  return out;
}

/** Per-vertex total weight of one bone — the input for influence visualization. */
export function boneInfluenceMask(skinData: SkinData, boneIndex: number): Float32Array {
  const mask = new Float32Array(skinData.vertexCount);
  for (let v = 0; v < skinData.vertexCount; v++) {
    const base = v * SLOTS;
    let total = 0;
    for (let slot = 0; slot < SLOTS; slot++) {
      if (skinData.skinIndex[base + slot] === boneIndex) total += skinData.skinWeight[base + slot];
    }
    mask[v] = total;
  }
  return mask;
}
